import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchPurchaseOrders } from '@/api/orders';
import type { PurchaseOrderListResponse } from '@/api/orders/types';
import { buildPurchaseOrdersApiBody } from '../-utils/listParams';

export const purchaseOrderKeys = {
  all: ['cascade-tracing', 'purchase-orders'] as const,
  list: (body: Record<string, unknown>) =>
    [...purchaseOrderKeys.all, 'list', body] as const,
};

export function purchaseOrdersQueryOptions(params: {
  page: number;
  limit: number;
  isArchived: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}) {
  const body = buildPurchaseOrdersApiBody(params);
  return queryOptions({
    queryKey: purchaseOrderKeys.list(body),
    queryFn: () => fetchPurchaseOrders(body),
    staleTime: 30_000,
  });
}

export function usePurchaseOrdersList(params: {
  page: number;
  limit: number;
  isArchived: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}) {
  const body = buildPurchaseOrdersApiBody(params);

  return useQuery({
    queryKey: purchaseOrderKeys.list(body),
    queryFn: () => fetchPurchaseOrders(body),
    staleTime: 30_000,
    refetchInterval: query => {
      const data = query.state.data as PurchaseOrderListResponse | undefined;
      const orders = data?.data ?? [];
      const hasProcessing = orders.some(
        o =>
          o.aiSummary?.docType?.status === 'PREPARING' ||
          o.aiSummary?.docType?.status === 'PROCESSING' ||
          o.aiSummary?.docContent?.status === 'PREPARING' ||
          o.aiSummary?.docContent?.status === 'PROCESSING' ||
          o.aiSummary?.scAnalysis?.status === 'PREPARING' ||
          o.aiSummary?.scAnalysis?.status === 'PROCESSING',
      );
      return hasProcessing ? 20_000 : false;
    },
  });
}
